/* eslint-disable react/style-prop-object */
import useBrazilianRealEstateFundIssuer from "components/company/v3/context/hooks/useBrazilianRealEstateFundIssuer";
import SadFaceIcon from "components/icons/SadFaceIcon";
import SortIcon from "components/icons/SortIcon";
import classNames from "components/ui/classNames";
import { ScrollArea } from "components/ui/ScrollArea";
import useTheme from "components/ui/useTheme";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import usePagination from "lib/hooks/usePagination";
import filterUnionByTypename from "lib/utils/filterUnionByTypename";
import orderBy from "lodash/orderBy";
import { ArrowDown10Icon, ArrowUp01Icon } from "lucide-react";
import { matchSorter } from "match-sorter";
import numeral from "numeral";
import * as React from "react";
import { useState } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import colors from "tailwindcss/colors";

interface FiisAssetsContentProps {
  isRenderPageDefault: boolean;
}

export default function FiisAssetsContent({ isRenderPageDefault }: FiisAssetsContentProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<
    | "location.city"
    | "location.state"
    | "area"
    | "vacancyRate"
    | "percentOfFundRevenue"
    | "percentOfPropertyRevenue"
    | ""
  >("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "">("");

  const theme = useTheme();
  const realEstateFund = useBrazilianRealEstateFundIssuer();

  const searchResults = matchSorter(
    filterUnionByTypename(realEstateFund.properties, "BrazilianRealEstateFundPropertyToRent"),
    searchQuery,
    {
      keys: ["name", "location.formatted", "location.abbreviatedStateName", "location.city", "location.state"],
      threshold: matchSorter.rankings.CONTAINS,
      keepDiacritics: false,
    },
  );

  const propertiesToRent =
    sortBy === "" && sortDirection === "" ? searchResults : orderBy(searchResults, [sortBy], [sortDirection as any]);

  const pagination = usePagination({
    items: propertiesToRent,
    itemsPerPage: isRenderPageDefault ? 8 : 20,
  });

  if (!hasPropertiesToRent(realEstateFund.properties)) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-slate-400">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-fit w-full gap-y-4">
      <div className="flex flex-col gap-4 md:flex-row-reverse">
        <div className="grid w-full grid-cols-4 justify-center xl:w-80">
          <div className="grid gap-y-1 text-sm">
            <strong className="text-center font-bold">{realEstateFund.overview.totalOfRealEstateProperties}</strong>
            <span className="text-center text-[#4e657f] dark:text-slate-400">
              {realEstateFund.overview.totalOfRealEstateProperties === 1 ? "Imóvel" : "Imóveis"}
            </span>
          </div>

          <div className="grid gap-y-1 text-sm">
            <strong className="text-center font-bold">{realEstateFund.overview.totalOfStates}</strong>
            <span className="text-center text-[#4e657f] dark:text-slate-400">
              {realEstateFund.overview.totalOfStates === 1 ? "Estado" : "Estados"}
            </span>
          </div>

          <div className="grid gap-y-1 text-sm">
            <strong className="text-center font-bold">{realEstateFund.overview.totalOfRegions}</strong>
            <span className="text-center text-[#4e657f] dark:text-slate-400">
              {realEstateFund.overview.totalOfRegions === 1 ? "Região" : "Regiões"}
            </span>
          </div>

          <div className="grid gap-y-1 text-sm">
            <strong className="text-center font-bold">
              {numeral(realEstateFund.overview.totalOfGrossLeaseableArea).format("0m000").replace(",", ".")}
            </strong>
            <span className="text-center text-[#4e657f] dark:text-slate-400">ABL (m²)</span>
          </div>
        </div>

        <input
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          placeholder="Digite para pesquisar os imóveis..."
          maxLength={18}
          value={searchQuery}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.currentTarget.value)}
          className={classNames(
            "box-border h-12 w-full border border-neutral-300 px-2 py-4 outline-none lg:rounded-3xl lg:px-5",
            "rounded-md placeholder:text-[#bfccda] focus:border-blue-600",
            "dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200",
            "dark:placeholder:text-slate-500 dark:focus:border-blue-500",
          )}
        />
      </div>

      <ScrollArea type="hover" orientation="horizontal" className="relative">
        <div>
          <table className="w-full min-w-md table-fixed border-collapse border-spacing-0">
            <thead>
              <tr className={theme === "dark" ? "bg-slate-800" : "bg-slate-50"}>
                <th className="py-3 text-sm font-bold text-[#293b51] dark:text-slate-300">Imóvel</th>

                <th className="py-3 text-sm font-bold text-[#293b51] dark:text-slate-300">Endereço</th>

                <th
                  className="w-1/6 cursor-pointer select-none py-3 text-sm font-bold text-[#293b51] dark:text-slate-300"
                  onClick={() => {
                    if (sortDirection === "") {
                      setSortBy("location.city");
                      setSortDirection("asc");
                    } else if (sortDirection === "asc") {
                      setSortBy("location.city");
                      setSortDirection("desc");
                    } else if (sortDirection === "desc") {
                      setSortBy("");
                      setSortDirection("");
                    }
                  }}
                >
                  <div className="grid grid-cols-[max-content_max-content] items-center justify-center gap-x-1 text-center">
                    <span>Cidade</span>
                    {sortDirection === "" && <SortIcon />}
                    {sortBy === "location.city" && sortDirection === "asc" && <ArrowUp01Icon strokeWidth={1.5} />}
                    {sortBy === "location.city" && sortDirection === "desc" && <ArrowDown10Icon strokeWidth={1.5} />}
                  </div>
                </th>

                <th
                  className="w-[8%] cursor-pointer select-none py-3 text-sm font-bold text-[#293b51] dark:text-slate-300"
                  onClick={() => {
                    if (sortDirection === "") {
                      setSortBy("location.state");
                      setSortDirection("asc");
                    } else if (sortDirection === "asc") {
                      setSortBy("location.state");
                      setSortDirection("desc");
                    } else if (sortDirection === "desc") {
                      setSortBy("");
                      setSortDirection("");
                    }
                  }}
                >
                  <div className="grid grid-cols-[max-content_max-content] items-center justify-center gap-x-1 text-center">
                    <span>Estado</span>
                    {sortDirection === "" && <SortIcon />}
                    {sortBy === "location.state" && sortDirection === "asc" && <ArrowUp01Icon strokeWidth={1.5} />}
                    {sortBy === "location.state" && sortDirection === "desc" && <ArrowDown10Icon strokeWidth={1.5} />}
                  </div>
                </th>

                <th
                  className="w-[10%] cursor-pointer select-none py-3 text-sm font-bold text-[#293b51] dark:text-slate-300"
                  onClick={() => {
                    if (sortDirection === "") {
                      setSortBy("area");
                      setSortDirection("asc");
                    } else if (sortDirection === "asc") {
                      setSortBy("area");
                      setSortDirection("desc");
                    } else if (sortDirection === "desc") {
                      setSortBy("");
                      setSortDirection("");
                    }
                  }}
                >
                  <div className="grid grid-cols-[max-content_max-content] items-center justify-center gap-x-1 text-center">
                    <span>Área (m²)</span>

                    {sortDirection === "" && <SortIcon />}
                    {sortBy === "area" && sortDirection === "asc" && <ArrowUp01Icon strokeWidth={1.5} />}
                    {sortBy === "area" && sortDirection === "desc" && <ArrowDown10Icon strokeWidth={1.5} />}
                  </div>
                </th>

                {propertiesToRent.some((property) => property.vacancyRate !== null) && (
                  <th
                    className="w-[7%] cursor-pointer select-none px-2 py-3 text-sm font-bold text-[#293b51] dark:text-slate-300"
                    onClick={() => {
                      if (sortDirection === "") {
                        setSortBy("vacancyRate");
                        setSortDirection("asc");
                      } else if (sortDirection === "asc") {
                        setSortBy("vacancyRate");
                        setSortDirection("desc");
                      } else if (sortDirection === "desc") {
                        setSortBy("");
                        setSortDirection("");
                      }
                    }}
                  >
                    <div className="grid grid-cols-[max-content_max-content] items-center justify-center gap-x-1 text-center">
                      <span>Vacância</span>

                      {sortDirection === "" && <SortIcon />}
                      {sortBy === "vacancyRate" && sortDirection === "asc" && <ArrowUp01Icon strokeWidth={1.5} />}
                      {sortBy === "vacancyRate" && sortDirection === "desc" && <ArrowDown10Icon strokeWidth={1.5} />}
                    </div>
                  </th>
                )}

                {propertiesToRent.some((property) => property.percentOfPropertyRevenue !== null) && (
                  <th
                    className="w-[7%] cursor-pointer select-none px-2 py-3 text-sm font-bold text-[#293b51] dark:text-slate-300"
                    onClick={() => {
                      if (sortDirection === "") {
                        setSortBy("percentOfPropertyRevenue");
                        setSortDirection("asc");
                      } else if (sortDirection === "asc") {
                        setSortBy("percentOfPropertyRevenue");
                        setSortDirection("desc");
                      } else if (sortDirection === "desc") {
                        setSortBy("");
                        setSortDirection("");
                      }
                    }}
                  >
                    <div className="grid grid-cols-[max-content_max-content] items-center justify-center gap-x-1 text-center">
                      <span>% Imóvel</span>

                      {sortDirection === "" && <SortIcon />}
                      {sortBy === "percentOfPropertyRevenue" && sortDirection === "asc" && (
                        <ArrowUp01Icon strokeWidth={1.5} />
                      )}
                      {sortBy === "percentOfPropertyRevenue" && sortDirection === "desc" && (
                        <ArrowDown10Icon strokeWidth={1.5} />
                      )}
                    </div>
                  </th>
                )}

                {propertiesToRent.some((property) => property.percentOfFundRevenue !== null) && (
                  <th
                    className="w-[7%] cursor-pointer select-none px-2 py-3 text-sm font-bold text-[#293b51] dark:text-slate-300"
                    onClick={() => {
                      if (sortDirection === "") {
                        setSortBy("percentOfFundRevenue");
                        setSortDirection("asc");
                      } else if (sortDirection === "asc") {
                        setSortBy("percentOfFundRevenue");
                        setSortDirection("desc");
                      } else if (sortDirection === "desc") {
                        setSortBy("");
                        setSortDirection("");
                      }
                    }}
                  >
                    <div className="grid grid-cols-[max-content_max-content] items-center justify-center gap-x-1 text-center">
                      <span>% Fundo</span>

                      {sortDirection === "" && <SortIcon />}
                      {sortBy === "percentOfFundRevenue" && sortDirection === "asc" && (
                        <ArrowUp01Icon strokeWidth={1.5} />
                      )}
                      {sortBy === "percentOfFundRevenue" && sortDirection === "desc" && (
                        <ArrowDown10Icon strokeWidth={1.5} />
                      )}
                    </div>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {pagination.currentItems.length === 0 && searchQuery !== "" && (
                <tr>
                  <td
                    colSpan={7}
                    className="max-w-0 p-4 text-center text-sm font-medium tracking-wide text-[#293b51] dark:text-slate-400"
                  >
                    Nenhum resultado encontrado.
                  </td>
                </tr>
              )}

              {pagination.currentItems
                .map((property, index) => {
                  const getRowBackgroundColor = () => {
                    if (index % 2 === 0) {
                      return "none";
                    }
                    return theme === "dark" ? colors.slate[800] : colors.slate[50];
                  };

                  if (!property.location) {
                    return (
                      <tr
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${property.id}/${index}`}
                        style={{
                          backgroundColor: getRowBackgroundColor(),
                        }}
                      >
                        <td className="max-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          <span title={property.name}>{property.name}</span>
                        </td>

                        <td className="max-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          {property.address}
                        </td>

                        <td className="w-1/6 max-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          -
                        </td>

                        <td className="w-[8%] max-w-0 px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          -
                        </td>

                        <td className="w-[10%] max-w-0 px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          {numeral(property.area).format("0,000").replace(",", ".")}
                        </td>

                        {property.vacancyRate !== null && (
                          <td className="w-[7%] max-w-0 px-2 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                            {(property.vacancyRate * 100).toFixed(0)}%
                          </td>
                        )}

                        {property.percentOfPropertyRevenue !== null && (
                          <td className="w-[7%] max-w-0 px-2 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                            <FormattedNumber
                              style="percent"
                              maximumFractionDigits={1}
                              value={property.percentOfPropertyRevenue}
                            />
                          </td>
                        )}

                        {property.percentOfFundRevenue !== null && (
                          <td className="w-[7%] max-w-0 px-2 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                            <FormattedNumber
                              style="percent"
                              maximumFractionDigits={1}
                              value={property.percentOfFundRevenue}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  }

                  return (
                    <tr
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${property.id}/${index}`}
                      style={{
                        backgroundColor: getRowBackgroundColor(),
                      }}
                    >
                      <td className="max-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                        <span title={property.name}>{property.name}</span>
                      </td>

                      <td className="max-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                        <span title={property.address}>{property.location.formatted}</span>
                      </td>

                      <td className="w-1/6 max-w-0 overflow-hidden text-ellipsis whitespace-nowrap px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                        {property.location.city ? (
                          <span title={property.location.city}>{property.location.city}</span>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="w-[8%] max-w-0 px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                        {property.location.abbreviatedStateName || "-"}
                      </td>

                      <td className="w-[10%] max-w-0 px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                        {numeral(property.area).format("0,000").replace(",", ".")}
                      </td>

                      {property.vacancyRate !== null && (
                        <td className="w-[12%] max-w-0 px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          {(property.vacancyRate * 100).toFixed(0)}%
                        </td>
                      )}

                      {property.percentOfPropertyRevenue !== null && (
                        <td className="w-[12%] max-w-0 px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          <FormattedNumber
                            style="percent"
                            maximumFractionDigits={1}
                            value={property.percentOfPropertyRevenue}
                          />
                        </td>
                      )}

                      {property.percentOfFundRevenue !== null && (
                        <td className="w-[12%] max-w-0 px-4 py-2 text-center text-sm font-medium tracking-wider text-[#293b51] dark:text-slate-300">
                          <FormattedNumber
                            style="percent"
                            maximumFractionDigits={1}
                            value={property.percentOfFundRevenue}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })
                .filter(Boolean)}
            </tbody>
          </table>
        </div>
      </ScrollArea>

      {pagination.pages.length > 1 && (
        <div className="px-4 text-right">
          <div className="inline-grid auto-cols-max grid-flow-col gap-x-3">
            {pagination.pages.map((page) => {
              const isActive = pagination.currentPage === page;

              return (
                <button
                  type="button"
                  key={page}
                  onClick={() => pagination.setCurrentPage(page)}
                  className={classNames(
                    "m-0 inline-flex items-center justify-center rounded-xl border-none",
                    "h-8 w-8 cursor-pointer select-none p-0 text-sm font-medium outline-none",
                    {
                      "bg-blue-600 text-white dark:bg-blue-600 dark:text-white": isActive,
                      "bg-slate-100 text-blue-600 dark:bg-slate-700 dark:text-slate-300": !isActive,
                    },
                  )}
                >
                  {page}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function hasPropertiesToRent(properties: IBrazilianRealEstateFundTopLevelFragment["properties"]): boolean {
  return properties.some((property) => {
    return property.__typename === "BrazilianRealEstateFundPropertyToRent";
  });
}
